import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { rampColor } from '../common/theme/accents';

// Where the time goes, by checkpoint and by day. Dwell is measured from arriving at a checkpoint to
// being recorded at the next one, which is what an operator means by "they sat at the weighbridge
// for forty minutes" - not the journey to it. The last checkpoint of a trip has no successor and so
// contributes no dwell; a truck that has not moved on yet is not evidence of how long the stage
// takes, and counting it as though it had finished would flatter every open trip.
const dayKey = (value) => {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  // Local time, as everywhere else in the interface: a shift belongs to the day the plant was in.
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
};

export const stays = (trips) => {
  const out = [];
  (trips || []).forEach((trip) => {
    const path = (trip.detections || [])
      .filter((d) => !d.deviation && d.detectedAt)
      .sort((a, b) => Date.parse(a.detectedAt) - Date.parse(b.detectedAt));
    path.forEach((detection, index) => {
      const next = path[index + 1];
      if (!next) return;
      // The leg to a customer site is the road journey, not time spent at the gate the truck left
      // by. Counting it would put the exit gate at the top of every table by hours.
      if (next.type === 'CLIENT') return;
      const minutes = (Date.parse(next.detectedAt) - Date.parse(detection.detectedAt)) / 60000;
      const day = dayKey(detection.detectedAt);
      if (!day || !Number.isFinite(minutes) || minutes < 0) return;
      out.push({
        checkpoint: detection.name || `Checkpoint ${detection.checkpointId}`, day, minutes, tripId: trip.id, order: index,
      });
    });
  });
  return out;
};

const metrics = {
  mean: { label: 'Mean', of: (values) => values.reduce((a, b) => a + b, 0) / values.length, unit: 'min' },
  worst: { label: 'Longest', of: (values) => Math.max(...values), unit: 'min' },
  count: { label: 'Visits', of: (values) => values.length, unit: '' },
};

const DwellHeatmap = ({ trips }) => {
  const [metric, setMetric] = useState('mean');
  const model = useMemo(() => {
    const rows = stays(trips);
    if (!rows.length) return null;
    const days = [...new Set(rows.map((r) => r.day))].sort();
    // Checkpoints in the order trucks meet them, so the table reads as the process does rather
    // than by whichever stage happens to be worst this week.
    const sequence = new Map();
    rows.forEach((r) => sequence.set(r.checkpoint, Math.min(sequence.get(r.checkpoint) ?? r.order, r.order)));
    const checkpoints = [...sequence.keys()].sort((a, b) => sequence.get(a) - sequence.get(b) || a.localeCompare(b));
    const at = (checkpoint, day) => rows.filter((r) => r.checkpoint === checkpoint && (!day || r.day === day)).map((r) => r.minutes);
    const value = (values) => (values.length ? metrics[metric].of(values) : null);
    const grid = checkpoints.map((checkpoint) => ({
      checkpoint,
      cells: days.map((day) => ({ day, value: value(at(checkpoint, day)), trips: at(checkpoint, day).length })),
      overall: value(at(checkpoint, null)),
    }));
    const peak = Math.max(...grid.flatMap((r) => r.cells.map((c) => c.value ?? 0)), 0);
    return {
      days, grid, peak, total: rows.length,
    };
  }, [trips, metric]);
  // A single scale across the whole table, so a hot cell is hot compared with every other cell and
  // not only with its own row.
  const shade = (value) => (value == null || !model?.peak
    ? 'transparent'
    : (theme) => rampColor(theme, value / model.peak, metric === 'count' ? 'count' : 'duration'));
  const show = (value) => (value == null ? '' : `${Math.round(value)}`);
  // A shaded table without a key asks the reader to guess what dark means. Five steps of the same
  // scale the cells use, with the two ends written out, which is all a key has to be.
  const legend = model?.peak ? [0.2, 0.4, 0.6, 0.8, 1].map((step) => step * model.peak) : [];
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">Time spent at each checkpoint</Typography>
          <Typography variant="body2" color="text.secondary">
            {metric === 'count' ? 'Recorded visits per day.' : 'Minutes from arriving at a checkpoint to being recorded at the next one, by day.'}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={metric} onChange={(event, next) => next && setMetric(next)}>
          {Object.entries(metrics).map(([key, m]) => <ToggleButton key={key} value={key}>{m.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>
      {!model ? (
        <Typography color="text.secondary" sx={{ py: 3 }}>
          No completed checkpoint stages in this selection. A stage counts once the truck has been recorded at the checkpoint after it.
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: model.days.length * 78 + 220 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', p: 1, position: 'sticky', left: 0, bgcolor: 'background.paper', minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">Checkpoint</Typography>
                  </Box>
                  {model.days.map((day) => (
                    <Box key={day} component="th" sx={{ p: 1, minWidth: 74 }}>
                      <Typography variant="caption" color="text.secondary">{day.slice(5)}</Typography>
                    </Box>
                  ))}
                  <Box component="th" sx={{ p: 1, minWidth: 74 }}><Typography variant="caption" color="text.secondary">All days</Typography></Box>
                </Box>
              </Box>
              <Box component="tbody">
                {model.grid.map((row) => (
                  <Box component="tr" key={row.checkpoint}>
                    <Box component="td" sx={{ p: 1, position: 'sticky', left: 0, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{row.checkpoint}</Typography>
                    </Box>
                    {row.cells.map((cell) => (
                      <Tooltip key={cell.day} title={cell.value == null ? `${row.checkpoint} · ${cell.day} · nothing recorded` : `${row.checkpoint} · ${cell.day} · ${metrics[metric].label.toLowerCase()} ${Math.round(cell.value)}${metrics[metric].unit && ` ${metrics[metric].unit}`} over ${cell.trips} stage${cell.trips === 1 ? '' : 's'}`}>
                        <Box component="td" sx={{ p: 1, textAlign: 'center', bgcolor: shade(cell.value), borderTop: 1, borderColor: 'divider', fontVariantNumeric: 'tabular-nums' }}>
                          <Typography variant="body2">{show(cell.value)}</Typography>
                        </Box>
                      </Tooltip>
                    ))}
                    <Box component="td" sx={{ p: 1, textAlign: 'center', borderTop: 1, borderColor: 'divider', fontVariantNumeric: 'tabular-nums' }}>
                      <Typography variant="body2" fontWeight={600}>{show(row.overall)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">0</Typography>
            {legend.map((value) => (
              <Box key={value} sx={{ width: 26, height: 12, bgcolor: shade(value), border: 1, borderColor: 'divider' }} />
            ))}
            <Typography variant="caption" color="text.secondary">
              {`${Math.round(model.peak)}${metrics[metric].unit ? ` ${metrics[metric].unit}` : ` visit${Math.round(model.peak) === 1 ? '' : 's'}`}`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {`${model.total} completed stages across ${model.grid.length} checkpoints. Stronger colour is ${metric === 'count' ? 'more visits' : metric === 'worst' ? 'a longer worst stage' : 'a longer average'}, relative to this selection, not an SLA breach. Filters above apply.`}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default DwellHeatmap;
